import { FastField, Field, useFormikContext } from 'formik';
import _ from 'lodash';
import React from 'react';
import * as Yup from 'yup';

import CustomInputDashboard from 'creative-components/CustomInput/CustomInputDashboard';
import GridContainer from 'creative-components/Grid/GridContainer';
import GridItem from 'creative-components/Grid/GridItem';

import AddressAutocomplete from 'components/AddressAutocomplete/AddressAutocomplete';

// Nasty code required since fields at this point can be something
// like primaryContact.address.address1
const errorCheck = (name, touched, errors, dirty, showErrorsWhen) => {
  let check = true; // showErrorsWhen = always

  if (showErrorsWhen === 'touched') {
    check = Boolean(name.split('.').reduce((o, i) => o[i] || false, touched));
  } else if (showErrorsWhen === 'formDirty') {
    check = dirty;
  }

  return check && Boolean(name.split('.').reduce((o, i) => o[i] || false, errors));
};

// showErrorsWhen = always | formDirty | touched
// customDirty is a dirty function to use for the dirty check
const AddressForm = ({
  classes,
  valueName,
  addressDisplayName,
  initialValue,
  showErrorsWhen = 'touched',
  disabled = false,
  customDirty = null,
  showClearButton = false,
}) => {
  const {
    dirty, setFieldValue, touched, errors,
  } = useFormikContext();

  const dirtyCheck = customDirty !== null ? customDirty : dirty;

  return (
    <GridContainer>
      <GridItem xs={12} md={8}>
        {/* Normal field here so that it updates on the dirty checks, if applicable */}
        <Field
          name={`${valueName}.address1`}
        >
          {({ field }) => (
            <AddressAutocomplete
              hasMultipleAddressFields
              valueName={valueName}
              setFieldValue={setFieldValue}
              labelText={addressDisplayName}
              initialValue={initialValue}
              inputProps={{
                ...field,
                autoComplete: 'forceoff',
              }}
              disabled={disabled}
              showClearButton={showClearButton}
              error={errorCheck(field.name, touched, errors, dirtyCheck, showErrorsWhen)}
            />
          )}
        </Field>
      </GridItem>
      <GridItem xs={12} md={4}>
        <FastField
          name={`${valueName}.address2`}
        >
          {({ field }) => (
            <CustomInputDashboard
              labelText="Apt, Suite"
              inputProps={{
                ...field,
                disabled,
              }}
            />
          )}
        </FastField>
      </GridItem>
      <GridItem xs={12} md={4}>
        <FastField
          name={`${valueName}.city`}
        >
          {({ field }) => (
            <CustomInputDashboard
              labelText="City"
              inputProps={{
                ...field,
                disabled: true,
              }}
              labelDisabled={disabled || false}
              error={errorCheck(field.name, touched, errors, dirtyCheck, showErrorsWhen)}
            />
          )}
        </FastField>
      </GridItem>
      <GridItem xs={12} md={4}>
        <FastField
          name={`${valueName}.state`}
        >
          {({ field }) => (
            <CustomInputDashboard
              labelText="State"
              inputProps={{
                ...field,
                disabled: true,
              }}
              labelDisabled={disabled || false}
              error={errorCheck(field.name, touched, errors, dirtyCheck, showErrorsWhen)}
            />
          )}
        </FastField>
      </GridItem>
      <GridItem xs={12} md={4}>
        <FastField
          name={`${valueName}.zipcode`}
        >
          {({ field }) => (
            <CustomInputDashboard
              labelText="Zipcode"
              inputProps={{
                ...field,
                disabled: true,
              }}
              labelDisabled={disabled || false}
              error={errorCheck(field.name, touched, errors, dirtyCheck, showErrorsWhen)}
            />
          )}
        </FastField>
      </GridItem>
      <FastField
        type="hidden"
        name={`${valueName}.location`}
      />
    </GridContainer>
  );
};

export const initialAddress = {
  address1: '',
  address2: '',
  city: '',
  state: '',
  zipcode: '',
  location: {},
};

/**
 * Extracts key from usually a MongoDB Address document to work with the AddressForm component
 *
 * @param {*} addressObj
 * @returns An address object compatible with the AddressForm component
 */
export const pickAddress = (addressObj) => ({
  ..._.pick(addressObj, ['address1', 'address2', 'city', 'state', 'zipcode', 'location']),
});

// Base address fields shared by all address schemas
const baseAddressFields = {
  address1: Yup.string().required(),
  address2: Yup.string(),
  city: Yup.string().required(),
  state: Yup.string().required(),
  zipcode: Yup.string().required(),
};

// Factory to build address schemas with optional location and lat/lng requirements
const makeAddressSchema = ({ includeLocation = true, latLngRequired = true } = {}) => Yup.object({
  ...baseAddressFields,
  ...(includeLocation
    ? {
      location: Yup.object({
        lat: latLngRequired ? Yup.number().required() : Yup.number().optional(),
        lng: latLngRequired ? Yup.number().required() : Yup.number().optional(),
      }).required(),
    }
    : {}),
});

// Backwards-compatible exports
export const addressYupValidation = makeAddressSchema({ includeLocation: true, latLngRequired: true });

export const addressOptionalLatLngYupValidation = makeAddressSchema({ includeLocation: true, latLngRequired: false });

export const addressYupValidationWithoutLocation = makeAddressSchema({ includeLocation: false });

export default AddressForm;
